import { motion } from 'framer-motion';
import React, { useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import ProjectsData from './ProjectsData';

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const stripHtml = (html = '') => html.replace(/<[^>]*>/g, '');

// Friendly names callers can pass -> the exact `tag` value stored on each
// ProjectsData entry ('app' | 'web'). Add more aliases if needed.
const CATEGORY_ALIASES = {
  app: 'app',
  apps: 'app',
  mobile: 'app',
  'app-development': 'app',
  web: 'web',
  website: 'web',
  'web-development': 'web',
};

const resolveTag = (category) => {
  if (!category) return null;
  return CATEGORY_ALIASES[category.toLowerCase()] || null;
};

const SimilarProjects = ({ category, currentId, count = 3 }) => {
  const navigate = useNavigate();
  const [showBtn, setShowBtn] = useState(true);
  const path = useLocation();

  useEffect(() => {
    setShowBtn(path.pathname !== '/portfolio');
  }, [path.pathname]);

  const projects = useMemo(() => {
    const tag = resolveTag(category);
    if (!tag) return []; // no/unknown category -> render nothing

    const list = Object.entries(ProjectsData)
      .filter(([id, data]) => data.tag === tag && id !== currentId)
      .map(([id, data]) => ({
        id,
        title: stripHtml(data.title) || id,
        description: data.desc || '',
        image: data.thumbnail,
      }));

    return shuffle(list).slice(0, count);
  }, [category, currentId, count]);

  const [hoverId, setHoverId] = useState('');

  if (!projects.length) return null;

  return (
    <div className='mx-auto w-[88%] py-10 sm:py-20'>
      <div className='flex justify-between items-center mb-6'>
        <h2 className="mont text-2xl font-semibold tracking-tight text-[#454648] sm:text-[48px]">
          Featured Projects
        </h2>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 '>
        {projects.map((item, idx) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: idx * 0.15 }}
            viewport={{ once: true }}
            className='group relative cursor-pointer rounded-xl overflow-hidden'
            onMouseEnter={() => setHoverId(item.id)}
            onMouseLeave={() => setHoverId('')}
            onClick={() => navigate(`/portfolio/${item.id}`)}
          >
            <div className='relative w-full bg-[#F5F5F7]'>
              <img className='w-full h-auto block' src={item.image} alt={item.title} />

              <div
                className={`absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent
                  transition-opacity duration-300
                  ${hoverId === item.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
              />

              <div
                className={`absolute bottom-4 left-4 right-4 text-white
                  transition-all duration-300
                  ${hoverId === item.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5 group-hover:opacity-100 group-hover:translate-y-0'}`}
              >
                <h3 className='text-xl font-semibold mb-1 capitalize'>{item.title}</h3>
                <p className='text-xs leading-relaxed line-clamp-2 text-white/80'>{item.description}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {showBtn && (
        <div
          onClick={() => navigate('/portfolio')}
          className='flex bg-[#4AC3D5] hover:bg-[#36b1c4] transition-colors duration-250
                     w-fit mx-auto px-9 py-2.5 rounded-full items-center justify-center gap-3
                     cursor-pointer mt-10'
        >
          <p className='text-sm sm:text-base text-white font-semibold hover:underline'>
            View Our All Projects
          </p>
          <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1.13243 11.3409C0.389989 8.56076 1.10931 5.47146 3.29039 3.29039C6.54476 0.0360171 11.8211 0.0360171 15.0755 3.29039C18.3299 6.54476 18.3299 11.8211 15.0755 15.0755C12.8944 17.2566 9.80512 17.9759 7.02498 17.2335M11.6831 11.683V6.68303M11.6831 6.68303H6.68306M11.6831 6.68303L3.34957 15.0163"
              stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </div>
  )
}

export default SimilarProjects